import React, { useState } from "react";
import { Card, Button, Input, Dropdown } from "antd";
import { PlusOutlined, EditOutlined, EllipsisOutlined, DeleteOutlined } from "@ant-design/icons";
import { MdSwapCalls } from "react-icons/md";
import SectionPhotoGrid from "./SectionPhotoGrid";

const PhotoSection = React.memo(({
    section,
    sectionIndex,
    projectData,
    coverSettings,
    onUpdateTitle,
    onUpdateSummary,
    onDeleteSection,
    onRearrangePhotos,
    onUpdateCoverPhoto,
    onDeletePhoto,
    onUpdateDescription,
    onAddPhotos,
    onInsertPhoto,
    deletingPhotoId,
    savingSectionTitle,
    savingSectionSummary,
    savingPhotoDescId
}) => {
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingSummary, setEditingSummary] = useState(false);
    const [tempTitle, setTempTitle] = useState(section.title);
    const [tempSummary, setTempSummary] = useState(section.summary || '');

    const handleSaveTitle = async () => {
        if (!tempTitle || tempTitle.trim() === '') {
            return;
        }
        await onUpdateTitle(sectionIndex, tempTitle);
        setEditingTitle(false);
    };

    const handleSaveSummary = async () => {
        await onUpdateSummary(sectionIndex, tempSummary);
        setEditingSummary(false);
    };

    const getPhotoSettingsMenu = () => {
        const menuItems = [
            {
                key: 'rearrange',
                label: 'Rearrange Photos',
                icon: <MdSwapCalls />,
                onClick: () => onRearrangePhotos(sectionIndex)
            }
        ];

        if (sectionIndex > 0) {
            menuItems.push({
                key: 'delete',
                label: 'Delete Section',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDeleteSection(sectionIndex)
            });
        }

        return { items: menuItems };
    };

    return (
        <div style={{ position: 'relative' }}>
            <div className="project-photo-reportsection-card-head" style={{ position: 'sticky', top: '0', backgroundColor: 'white', zIndex: 10, padding: '16px 24px', marginBottom: '16px', borderRadius: '8px 8px 0 0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: '200px' }}>
                    {editingTitle ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <Input
                                value={tempTitle}
                                onChange={(e) => setTempTitle(e.target.value)}
                                autoFocus
                                style={{ fontWeight: 'bold', fontSize: '18px', flex: 1 }}
                                placeholder="Enter section title"
                            />
                            <Button
                                type="primary"
                                size="small"
                                className="btn-GeneratePDF"
                                style={{ fontSize: '13px' }}
                                onClick={handleSaveTitle}
                                loading={savingSectionTitle}
                            >
                                Save
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setEditingTitle(false);
                                    setTempTitle(section.title);
                                }}
                                style={{ fontSize: '13px' }}
                            >
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <>
                            <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{section.title}</span>
                            <EditOutlined
                                style={{ marginLeft: '8px', color: '#1890ff', fontSize: '16px', cursor: 'pointer' }}
                                onClick={() => {
                                    setEditingTitle(true);
                                    setTempTitle(section.title);
                                }}
                            />
                        </>
                    )}
                </div>
                {!editingTitle && (
                    <Dropdown className="sectio-photo-option-menu" menu={getPhotoSettingsMenu()} trigger={['click']}>
                        <Button icon={<EllipsisOutlined />} />
                    </Dropdown>
                )}
            </div>
            <Card className="project-photo-reportsection-card"
                key={section.id}
                style={{ marginBottom: '24px', marginTop: '0' }}
            >
                <p style={{ color: '#0a497b', fontWeight: '600', fontSize: '16px', marginBottom: '16px' }}>
                    {section.photos.length} Photo(s)
                </p>

                {/* Photos Grid */}
                <SectionPhotoGrid
                    photos={section.photos}
                    sectionIndex={sectionIndex}
                    projectData={projectData}
                    coverSettings={coverSettings}
                    onUpdateCoverPhoto={onUpdateCoverPhoto}
                    onDeletePhoto={onDeletePhoto}
                    onUpdateDescription={onUpdateDescription}
                    onInsertPhoto={onInsertPhoto}
                    deletingPhotoId={deletingPhotoId}
                    savingPhotoDescId={savingPhotoDescId}
                />

                {/* Section Summary */}
                <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Section Summary</label>
                    {editingSummary ? (
                        <>
                            <Input.TextArea
                                rows={3}
                                value={tempSummary}
                                onChange={(e) => setTempSummary(e.target.value)}
                                autoFocus
                                placeholder="Add section summary..."
                                style={{ marginBottom: '8px', border: '1px solid #d9d9d9' }}
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    type="primary"
                                    className="btn-GeneratePDF"
                                    size="small"
                                    onClick={handleSaveSummary}
                                    loading={savingSectionSummary}
                                    style={{ fontSize: '13px' }}
                                >
                                    Save Summary
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setEditingSummary(false);
                                        setTempSummary(section.summary || '');
                                    }}
                                    style={{ fontSize: '13px' }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Input.TextArea
                            rows={3}
                            value={section.summary || 'Click to add section summary...'}
                            onClick={() => {
                                setEditingSummary(true);
                                setTempSummary(section.summary || '');
                            }}
                            readOnly
                            style={{ cursor: 'pointer', border: '1px solid #d9d9d9' }}
                        />
                    )}
                </div>

                {/* Add Photos Button */}
                <Button
                    className="add-photo-to-section"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => onAddPhotos(sectionIndex)}
                    style={{ marginTop: '16px' }}
                >
                    Add Photos to Section
                </Button>
            </Card>
        </div>
    );
});

PhotoSection.displayName = 'PhotoSection';

export default PhotoSection;


